import { Component, computed, inject, signal } from '@angular/core';
import {
  AssignmentType,
  PaginatedPickListBehaviour,
} from '@shared/form-controls/generic-pick-list/generic-pick-list.component';
import { BuildingPickListItemDto, BuildingService, QrCodeAssignmentDto, QrCodeService } from '@artemis-software/wr-api';
import { firstValueFrom, throwError, timeout } from 'rxjs';
import { Router } from '@angular/router';
import { NbToastrService } from '@nebular/theme';
import { catchError } from 'rxjs/operators';
import { downloadBlob } from '@/utils/csv-download';

const QR_PDF_TIMEOUT_MS = 180000;

@Component({
  standalone: false,
  selector: 'wr-qr-code-generate-page',
  templateUrl: './qr-code-generate-page.component.html',
  styleUrls: ['./qr-code-generate-page.component.scss'],
})
export class QrCodeGeneratePageComponent implements PaginatedPickListBehaviour<BuildingPickListItemDto> {

  readonly buildingService = inject(BuildingService);
  readonly router = inject(Router);
  readonly currentUrl = this.router.url;
  readonly nbToastrService = inject(NbToastrService);
  readonly qrCodeService = inject(QrCodeService);

  readonly offsetTop = signal<number>(0.0);
  readonly offsetLeft = signal<number>(0.0);
  readonly DesignEnum = QrCodeAssignmentDto.DesignEnum;
  readonly design = signal<QrCodeAssignmentDto.DesignEnum>(QrCodeAssignmentDto.DesignEnum.Gema);
  readonly includeVisible = signal(true);
  readonly includeHidden = signal(false);
  readonly noSectionKindSelected = computed(() => !this.includeVisible() && !this.includeHidden());

  readonly selectedBuildingAssignments = signal<BuildingPickListItemDto[]>([]);
  readonly loading = signal(false);

  isPaginated = true as const;

  async getAllItemsWithPagination(params?: { page?: number; size?: number; search?: string })
    : Promise<{ items: BuildingPickListItemDto[]; total: number }> {

    const items = await firstValueFrom(
      this.buildingService.findAll({
        deleted: false,
        page: params?.page,
        size: params?.size,
        fullTextSearch: params?.search,
        sort: { sort: 'sectionText', order: 'ASC' },
      }),
    );
    return ({
      items,
      total: items.length,
    });
  }

  getAssignmentType(): AssignmentType {
    return AssignmentType.UNASSIGNED;
  }

  getDisplayName(item: BuildingPickListItemDto): string {
    return item.baseData.sectionText || 'Kein Name vergeben';
  }

  getFirstAdditionalInfo(item: BuildingPickListItemDto): string {
    return item.internalNumber || 'Keine interne Nummer';
  }

  getThirdAdditionalInfo(item: BuildingPickListItemDto): string {
    return item.baseData.externalNumber || 'Keine externe Nummer';
  }

  async submit(): Promise<void> {
    if (this.loading() || this.noSectionKindSelected()) {
      return;
    }

    const assigned = this.selectedBuildingAssignments();
    if (!assigned.length) {
      this.nbToastrService.danger('Bitte wählen Sie mindestens ein Gebäude aus.', 'Fehler');
      return;
    }

    this.loading.set(true);
    try {
      const dto: QrCodeAssignmentDto = {
        buildingIds: assigned.map(b => b.id),
        offsetLeft: this.offsetLeft(),
        offsetTop: this.offsetTop(),
        includeVisible: this.includeVisible(),
        includeHidden: this.includeHidden(),
        design: this.design(),
      };

      const blob = await firstValueFrom(
        this.qrCodeService.downloadQrPdf(dto).pipe(
          timeout(QR_PDF_TIMEOUT_MS),
          catchError(error => {
            console.error('Error downloading QR PDF:', error);
            return throwError(() => error);
          }),
        ),
      );

      if (!blob || blob.size === 0) {
        this.nbToastrService.warning('Für die Auswahl gibt es keine passenden Hausnummern.', 'Keine QR-Codes');
        return;
      }

      downloadBlob(blob, 'qr-codes.pdf');

      this.nbToastrService.success('QR-Codes PDF erstellt', 'Erfolg');
    } catch (err) {
      console.error(err);
      this.nbToastrService.danger('Fehler beim PDF-Download', 'Fehler');
    } finally {
      this.loading.set(false);
    }
  }

  async cancel(): Promise<void> {
    await this.router.navigateByUrl('/', { skipLocationChange: true });
    await this.router.navigateByUrl(this.currentUrl);
  }

}
